import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw, Server, Radio } from "lucide-react";

const human = (iso) => {
  if (!iso) return "not yet";
  const secs = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  return `${Math.round(secs / 60)} min ago`;
};

const host = (url) => (url || "").replace(/^wss?:\/\//, "").replace(/\/$/, "") || "not set";

/** Which video server the class is on right now. */
export function MediaStatusCard() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .get("/admin/media-status")
      .then(({ data }) => setS(data))
      .catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  const recheck = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/media-status/recheck");
      setS(data);
      toast.success(data.primary_ok ? "Your server answered — all good" : "Your server is not answering");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const setMode = async (mode) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/media-status/override", { mode });
      setS(data);
      toast.success(
        mode === "auto"
          ? "Back to automatic — it switches on its own"
          : mode === "fallback"
          ? "Class pinned to the standby server"
          : "Class pinned to your own server"
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const onStandby = s?.active === "fallback";

  return (
    <Card
      className={`p-5 bg-[#0a0c0b] ring-1 border-0 ${
        onStandby ? "ring-amber-400/40" : "ring-white/[0.07]"
      }`}
      data-testid="media-status-card"
    >
      <div className="flex items-center gap-2.5">
        <Server
          className={`w-[18px] h-[18px] ${onStandby ? "text-amber-400" : "text-emerald-400"}`}
          strokeWidth={2}
        />
        <div
          className={`text-[10px] uppercase tracking-widest ${
            onStandby ? "text-amber-400" : "text-emerald-400"
          }`}
        >
          Video server
        </div>
      </div>

      <div className="mt-3 flex items-start gap-3">
        <span className="relative mt-[7px] shrink-0">
          <span
            className={`block w-2.5 h-2.5 rounded-full ${
              onStandby ? "bg-amber-400" : s ? "bg-emerald-400" : "bg-white/20"
            }`}
          />
          {s && (
            <span
              className={`absolute inset-0 rounded-full animate-ping ${
                onStandby ? "bg-amber-400/60" : "bg-emerald-400/60"
              }`}
            />
          )}
        </span>
        <div className="min-w-0">
          <div className="text-[14px] font-semibold text-white" data-testid="media-active-label">
            {!s
              ? "Checking…"
              : onStandby
              ? "Running on the standby server"
              : "Live on your own server"}
          </div>
          <div className="text-[12px] text-white/45 leading-relaxed" data-testid="media-active-host">
            {onStandby ? host(s?.fallback_url) : host(s?.primary_url)}
            {s?.override && s.override !== "auto" && (
              <span className="text-amber-300/80"> · pinned by you</span>
            )}
          </div>
          {s && !s.primary_ok && (
            <div className="mt-1 text-[11.5px] text-amber-300/80" data-testid="media-primary-error">
              Your server isn&apos;t answering{s.error ? ` — ${s.error}` : ""}. Students are being
              sent to the standby automatically.
            </div>
          )}
          <div className="mt-1 text-[11.5px] text-white/25">
            Checked {human(s?.checked_at)} · switches over on its own within a few seconds
            {s && !s.fallback_configured && " · no standby configured yet"}
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          onClick={recheck}
          disabled={busy}
          className="rounded-full h-10 px-4 bg-yellow-300 hover:bg-yellow-200 text-black text-[11px] font-bold uppercase tracking-[0.12em]"
          data-testid="media-recheck"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-2 ${busy ? "animate-spin" : ""}`} /> Check now
        </Button>
        {s?.override === "auto" ? (
          <Button
            onClick={() => setMode("fallback")}
            disabled={busy || !s?.fallback_configured}
            variant="ghost"
            className="rounded-full h-10 px-4 ring-1 ring-white/[0.12] text-[11px] uppercase tracking-[0.12em] text-white/60 hover:text-white"
            data-testid="media-force-standby"
          >
            <Radio className="w-3.5 h-3.5 mr-2" /> Move class to standby
          </Button>
        ) : (
          <Button
            onClick={() => setMode("auto")}
            disabled={busy}
            variant="ghost"
            className="rounded-full h-10 px-4 ring-1 ring-emerald-400/30 text-[11px] uppercase tracking-[0.12em] text-emerald-300 hover:text-white"
            data-testid="media-reset-auto"
          >
            Back to automatic
          </Button>
        )}
      </div>
    </Card>
  );
}
